import { z } from 'zod'
import { uuidSchema } from '../validators/uuid.validator.js'
import { clientStatusSchema } from '../enums/client-status.enum.js'
import { clientTypeSchema } from '../enums/client-type.enum.js'
import { contactDtoSchema } from './contact.dto.js'
import { siteDtoSchema } from './site.dto.js'

/**
 * A client — the entity we sell to and bill. For a COMMERCIAL client `name` is
 * the organization (e.g. "The Trails at Regal Park"); for a RESIDENTIAL one it
 * is the household's name. The people are always in `contacts`, never inlined
 * here, so both kinds have one shape.
 *
 * The address on a client is the BILLING address. Where work happens is a
 * `Site`, which is a separate record — a client can have several, and for
 * commercial work the two are routinely different places.
 */
export const clientDtoSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  type: clientTypeSchema,
  status: clientStatusSchema,

  addressLine1: z.string().nullable(),
  addressLine2: z.string().nullable(),
  city: z.string().nullable(),
  state: z.string().nullable(),
  postalCode: z.string().nullable(),

  notes: z.string().nullable(),

  /** Homes in the community, when recorded. */
  homeCount: z.number().int().positive().nullable(),

  /** The user who owns this relationship (the salesperson). */
  ownerId: uuidSchema.nullable(),
  ownerName: z.string().nullable(),

  /**
   * The Housecall Pro customer this client is, with its name as HCP had it
   * when linked. Null until linked. Jobs are created on it.
   */
  hcpCustomer: z.object({ id: z.string(), name: z.string() }).nullable(),

  /** Set when archived. Clients are never hard-deleted — proposals cite them. */
  archivedAt: z.string().datetime().nullable(),

  contactCount: z.number().int().nonnegative(),
  siteCount: z.number().int().nonnegative(),

  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
})

export type ClientDto = z.infer<typeof clientDtoSchema>

export const clientListDtoSchema = z.object({
  clients: z.array(clientDtoSchema),
  total: z.number().int().nonnegative(),
})

export type ClientListDto = z.infer<typeof clientListDtoSchema>

/** A client with its people and places, for the detail view. */
export const clientDetailDtoSchema = clientDtoSchema.extend({
  contacts: z.array(contactDtoSchema),
  sites: z.array(siteDtoSchema),
})

export type ClientDetailDto = z.infer<typeof clientDetailDtoSchema>
